import type { ThemeColors, Palettes } from '@library/stylesheets/theme';

// The public site's palettes, expressed in the SAME token vocabulary the phone
// apps use (@library/stylesheets/theme.ts defines the names and no values, so each
// app supplies its own). Values are kept in step with
// busLineApp/stylesheets/shared.ts — a marketing site that does not look like the
// product it is selling is worse than no marketing site.
//
// Byte-for-byte the same values busLineSrv uses, and copied rather than shared
// for the same reason its environment/ is copied: two apps that are allowed to
// diverge should not be able to repaint each other. If a third app ever wants
// these, that is the point to lift them into @library.
//
// ThemeProvider re-emits every token here as a CSS custom property, so global.css
// contains no colour literals at all.

export const lightColors: ThemeColors = {
    appBackground: '#f2f2f2',
    screenBackground: '#f5f5f5',
    surface: '#fff',
    surfaceSubtle: '#f5f6fa',
    surfaceMuted: '#f0f0f0',

    headerBackground: '#fff',
    headerSurface: '#eef1f7',
    headerActive: '#e3e7ef',
    headerBorder: '#dfe3ea',
    headerOutline: '#c9d0da',
    headerText: '#1f2430',
    headerTextMuted: '#8a91a1',

    primary: '#1565c0',
    primaryStrong: '#0d47a1',
    primaryBright: '#2f7ff7',
    primarySurface: '#e8f0fe',
    primarySurfaceSoft: '#f0f5ff',
    onPrimary: '#fff',
    disabledFill: '#ccc',
    neutralFill: '#888',

    textPrimary: '#222',
    textHeading: '#1f2430',
    textBody: '#2f3647',
    textSecondary: '#666',
    textMuted: '#8a91a1',
    textDisabled: '#9aa0ad',
    textOnBadge: '#333',

    border: '#e6e6e6',
    borderStrong: '#d6dbe5',
    divider: '#e6e9f0',
    primaryBorder: '#cfdfff',
    successBorder: '#c8e6c9',
    warningBorder: '#ffe082',
    dangerBorder: '#ffcdd2',
    swatchBorder: '#bdbdbd',

    danger: '#c62828',
    dangerFill: '#c62828',
    dangerSurface: '#fdecea',
    warning: '#e65100',
    warningFill: '#e65100',
    warningSurface: '#fff3e0',
    success: '#1b7a3d',
    successFill: '#2e7d32',
    successSurface: '#e8f5e9',
    notice: '#f57f17',
    noticeSurface: '#fff8e1',

    accentIndigo: '#3b5bdb',
    accentIndigoSurface: '#eef3ff',
    accentOrange: '#c2610a',
    accentOrangeSurface: '#fdf0e6',

    scale0: '#e0e0e0',
    scale1: '#90caf9',
    scale2: '#42a5f5',
    scale3: '#1565c0',

    backdrop: 'rgba(0,0,0,0.45)',
    shadow: '#000',
};

export const darkColors: ThemeColors = {
    appBackground: '#0e1013',
    screenBackground: '#0e1013',
    surface: '#171a1f',
    surfaceSubtle: '#12151a',
    surfaceMuted: '#22262e',

    headerBackground: '#08090b',
    headerSurface: '#23272f',
    headerActive: '#1a1d23',
    headerBorder: '#1c1f25',
    headerOutline: '#3a3f47',
    headerText: '#f2f4f7',
    headerTextMuted: '#8a929e',

    primary: '#2563eb',
    primaryStrong: '#7aabff',
    primaryBright: '#7aabff',
    primarySurface: '#16233a',
    primarySurfaceSoft: '#131d2e',
    onPrimary: '#fff',
    disabledFill: '#343a45',
    neutralFill: '#4a5261',

    textPrimary: '#e8eaef',
    textHeading: '#f2f4f8',
    textBody: '#d3d8e0',
    textSecondary: '#a0a8b6',
    textMuted: '#7f8797',
    textDisabled: '#5d6574',
    textOnBadge: '#e6e9ef',

    border: '#262a32',
    borderStrong: '#343a45',
    divider: '#22262e',
    primaryBorder: '#2a3a5c',
    successBorder: '#1e4a30',
    warningBorder: '#4a3a15',
    dangerBorder: '#5c2226',
    swatchBorder: '#4a5261',

    danger: '#f87171',
    dangerFill: '#b91c1c',
    dangerSurface: '#3a1c1e',
    warning: '#f5a524',
    warningFill: '#b45309',
    warningSurface: '#3a2a12',
    success: '#4ade80',
    successFill: '#15803d',
    successSurface: '#14301f',
    notice: '#fbbf24',
    noticeSurface: '#332a10',

    accentIndigo: '#94a8ff',
    accentIndigoSurface: '#1b2242',
    accentOrange: '#f0a35e',
    accentOrangeSurface: '#33220f',

    scale0: '#262a32',
    scale1: '#1a3a5c',
    scale2: '#1e5aa8',
    scale3: '#2f7ff7',

    backdrop: 'rgba(0,0,0,0.65)',
    shadow: '#000',
};

export const palettes: Palettes = {
    light: lightColors,
    dark: darkColors,
};
